import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input } from './input';

// Address search box for one-off custom stops. Queries the server-side Geoapify proxy
// (proximity-biased near 27410). The suggestion list renders in a body portal so it
// isn't clipped by the destinations table's overflow container.
export const AddressAutocomplete = ({ value, onChange, onPick, onKeyDown, api, token, placeholder = 'Search place or address…' }) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const reqIdRef = useRef(0);

  const place = useCallback(() => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom, width: r.width });
    }
  }, []);

  // Debounced fetch while open and the query is long enough
  useEffect(() => {
    if (!open) return undefined;
    const q = (value || '').trim();
    if (q.length < 3) { setResults([]); return undefined; }
    const id = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/geocode/autocomplete?q=${encodeURIComponent(q)}`, token)
        .then(rows => { if (id === reqIdRef.current) { setResults(rows || []); setActive(-1); } })
        .catch(() => { if (id === reqIdRef.current) setResults([]); })
        .finally(() => { if (id === reqIdRef.current) setLoading(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [value, open, api, token]);

  // Keep the portal anchored to the input while open
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => { window.removeEventListener('scroll', onMove, true); window.removeEventListener('resize', onMove); };
  }, [open, place]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const pick = (r) => { onPick(r); setOpen(false); setResults([]); };

  const handleKeyDown = (e) => {
    if (open && results.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); return; }
      if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(results[active]); return; }
    }
    if (onKeyDown) onKeyDown(e);
  };

  return (
    <>
      <Input
        ref={inputRef}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { if ((value || '').trim().length >= 3) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-8 text-sm"
      />
      {open && rect && (results.length > 0 || loading) && createPortal(
        <div
          ref={listRef}
          style={{ position: 'fixed', left: rect.left, top: rect.top + 2, width: Math.max(rect.width, 260), zIndex: 60 }}
          className="rounded-md border border-border bg-card text-card-foreground shadow-lg text-sm overflow-hidden"
        >
          {loading && results.length === 0 && <div className="px-3 py-2 text-muted-foreground text-xs">Searching…</div>}
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setActive(i)}
              className={`block w-full text-left px-3 py-1.5 ${i === active ? 'bg-accent' : 'hover:bg-accent'}`}
            >
              <div className="font-medium truncate">{r.line1}</div>
              {r.line2 && <div className="text-xs text-muted-foreground truncate">{r.line2}</div>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
